import { OidcClient, UserManager, UserManagerSettings, WebStorageStateStore } from 'oidc-client-ts';

import {
  resolveZitadelClientId,
  resolveZitadelIssuer,
  resolveZitadelProjectId,
} from '../../utils/core/endpoints';
import { isDefined } from '../../utils/variables';

/**
 *  The path Zitadel returns to after a completed login. Every branded host is registered with its
 *  own redirect URI on the Zitadel side, so the origin is taken from the browser rather than
 *  enumerated here.
 */
export const ZITADEL_CALLBACK_PATH = '/auth/callback';

/**
 *  Where Zitadel returns to after a completed sign-out.
 *
 *  Registered on the Zitadel side as the origin with a trailing slash, and matched by exact string
 *  like the callback above. It is the app root rather than `/signin` deliberately: the root is a
 *  protected route, so a signed-out user resolves to the sign-in page on arrival, and no second
 *  URI has to be registered for every branded host.
 */
export const ZITADEL_POST_LOGOUT_PATH = '/';

let userManager: UserManager | undefined;
let oidcClient: OidcClient | undefined;

/**
 *  The settings both the `UserManager` and the bare {@link getZitadelOidcClient} are built from.
 *
 *  One builder rather than two literals, because the two objects have to agree exactly: a request
 *  started through one is finished through the other, and a difference in `redirect_uri`, `scope` or
 *  the state store would show up only as a failed callback.
 *
 *  Reads the environment variables on each call, and throws where they are unset — see
 *  {@link getZitadelUserManager}.
 */
const buildZitadelSettings = (): UserManagerSettings => ({
  authority: resolveZitadelIssuer(),
  client_id: resolveZitadelClientId(),
  redirect_uri: `${window.location.origin}${ZITADEL_CALLBACK_PATH}`,
  post_logout_redirect_uri: `${window.location.origin}${ZITADEL_POST_LOGOUT_PATH}`,
  response_type: 'code',
  // The project audience scope puts this environment's project id in the access token's `aud`,
  // which the backend pins. Without it a token is rejected as belonging to no known project.
  scope: `openid profile email offline_access urn:zitadel:iam:org:project:id:${resolveZitadelProjectId()}:aud`,
  // `localStorage` rather than the `sessionStorage` default, so the refresh token outlives the
  // tab the way the access token in `LocalStorageKeys.Token` already does. With the default, a
  // second tab is signed in on the stored access token but holds nothing to renew it with, and
  // would sign the user out at expiry despite a refresh token that is good for 30 days.
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  // Stated rather than left to the default, now that two objects share it. It is the same place the
  // default would have chosen — `localStorage` under the `oidc.` prefix — so nothing in flight is
  // invalidated, but a future change to either object's defaults can no longer separate them.
  stateStore: new WebStorageStateStore({ store: window.localStorage }),
});

/**
 *  Returns the shared `UserManager` for the Zitadel authorization-code + PKCE flow.
 *
 *  Created on first use rather than at import time: the environment variables it reads are only
 *  set where the Zitadel login is actually configured, and resolving them throws when they are not.
 */
export const getZitadelUserManager = () => {
  if (!isDefined(userManager)) {
    userManager = new UserManager(buildZitadelSettings());
  }

  return userManager;
};

/**
 *  The same configuration as {@link getZitadelUserManager}, as the lower-level client.
 *
 *  Needed for exactly one thing: building an authorization request without navigating to it, which
 *  the invitation flow does so that the request can be started before the person is handed to
 *  Zitadel's own page (SK-7425). `UserManager` has no public equivalent — `signinRedirect` builds a
 *  request and leaves for it in the same call — while `OidcClient.createSigninRequest` builds one
 *  and stores its state, which is what makes the eventual callback completable.
 *
 *  Sharing {@link buildZitadelSettings} is what makes that safe: the state this writes is the state
 *  `UserManager.signinRedirectCallback` later looks for, in the same store under the same key.
 */
export const getZitadelOidcClient = () => {
  if (!isDefined(oidcClient)) {
    oidcClient = new OidcClient(buildZitadelSettings());
  }

  return oidcClient;
};

/**
 *  The shared `UserManager`, or `undefined` where this environment has no Zitadel configuration.
 *
 *  For callers that run on every page rather than in response to someone pressing "sign in":
 *  an unconfigured environment is a legitimate state for them, not a failure worth reporting, and
 *  {@link getZitadelUserManager} answers it with a throw.
 */
export const tryGetZitadelUserManager = () => {
  try {
    return getZitadelUserManager();
  } catch {
    return undefined;
  }
};

/**
 *  Whether the session being signed out of is a Zitadel one.
 *
 *  Asked before anything is cleared, so a caller knows in advance that sign-out ends in a redirect
 *  and can leave the screen it is showing alone until the browser leaves.
 *
 *  `false` rather than a throw where Zitadel is not configured at all: a legacy or Entra user must
 *  still be able to sign out of an environment that has never heard of Zitadel.
 */
export const hasZitadelSession = async () => {
  try {
    return isDefined(await getZitadelUserManager().getUser());
  } catch (error) {
    console.error('Could not tell whether this is a Zitadel session.', error);
    return false;
  }
};

/**
 *  Ends the Zitadel session for this browser, and reports whether it started a redirect to do so.
 *
 *  Clearing our own storage is not a sign-out: Zitadel keeps its own session, so the next person to
 *  press "sign in" on this browser is silently re-authenticated as the previous one. Ending it is
 *  OpenID Connect's RP-initiated logout — a full navigation away to Zitadel and back, so a caller
 *  that gets `true` must not navigate anywhere itself.
 *
 *  In a browser this does not return at all on the way out: `signoutRedirect` resolves only on
 *  `pageshow`, which fires when someone comes *back* to a page from the bfcache. Returning `false`
 *  therefore means Zitadel could not be reached and the caller still owes the user a local
 *  sign-out. `signoutRedirect` removes the stored user itself before it navigates.
 */
export const endZitadelSession = async () => {
  try {
    const manager = getZitadelUserManager();

    if (!isDefined(await manager.getUser())) {
      return false;
    }

    await manager.signoutRedirect();
    return true;
  } catch (error) {
    // Deliberately swallowed: sign-out has to finish locally whatever Zitadel does. The reason
    // goes to the console so a session that outlives a sign-out can be diagnosed.
    console.error('Zitadel sign-out could not be completed.', error);
    return false;
  }
};

/**
 *  Resets the shared `UserManager` and `OidcClient`. Only intended for tests.
 */
export const resetZitadelUserManager = () => {
  userManager = undefined;
  oidcClient = undefined;
};
